import React from 'react';
import { FormattedMessage } from '../../../../util/reactIntl';
import { LinkedLogo, NamedLink } from '../../../../components';

import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for blocks in footer (1-4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    socialMediaLinks = [],
    appearance,
    options,
    linkLogoToExternalSite,
  } = props;

  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  // use block builder instead of mapping blocks manually

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={options}
    >
      <div className={css.footer}>
        <div className={css.mainRow}>
          <div className={css.brand}>
            <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout={logoLayout}
            />
            <p className={css.slogan}>For the shelf-conscious.</p>
            <p className={css.description}>A Canadian marketplace for books and bookish finds.</p>
            {showSocialMediaLinks ? (
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
            ) : null}
          </div>
          <nav className={css.linkGroup} aria-label="Marketplace">
            <h2>Marketplace</h2>
            <NamedLink name="SearchPage">Marketplace</NamedLink>
            <NamedLink name="TrendingPage">Trending</NamedLink>
            <NamedLink name="ShopsPage">PageHop Shops</NamedLink>
            <NamedLink name="CMSPage" params={{ pageId: 'events' }}>
              Events
            </NamedLink>
            <NamedLink name="CMSPage" params={{ pageId: 'shelfy-social' }}>
              Booker’s Reading Club
            </NamedLink>
          </nav>
          <nav className={css.linkGroup} aria-label="Sell and support">
            <h2>Sell &amp; Support</h2>
            <NamedLink name="SellerHubPage">Sell on PageHop</NamedLink>
            <NamedLink name="HowToUseShelfyTopicPage" params={{ topic: 'selling' }}>
              How Selling Works
            </NamedLink>
            <NamedLink name="SellerHubPage">PageHop 101</NamedLink>
            <NamedLink name="HowToUseShelfyTopicPage" params={{ topic: 'shipping' }}>
              Shipping Guide
            </NamedLink>
            <NamedLink name="HowToUseShelfyTopicPage" params={{ topic: 'payments' }}>
              Fees &amp; Payouts
            </NamedLink>
            <NamedLink name="ContactPage">
              <FormattedMessage id="SectionFooter.contact" />
            </NamedLink>
            <NamedLink name="ReportPage">
              <FormattedMessage id="SectionFooter.report" />
            </NamedLink>
          </nav>
          <nav className={css.linkGroup} aria-label="PageHop Books">
            <h2>PageHop Books</h2>
            <NamedLink name="AboutPage">About PageHop Books</NamedLink>
            <NamedLink name="CMSPage" params={{ pageId: 'community-rules' }}>
              Rules
            </NamedLink>
            <NamedLink name="TermsOfServicePage">Terms of Service</NamedLink>
            <NamedLink name="PrivacyPolicyPage">Privacy Policy</NamedLink>
          </nav>
        </div>
        <div className={css.bottomRow}>© 2026 PageHop Books. All rights reserved.</div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
